import { useCallback } from "react";
import { useApi } from "src/Api/useApi";
import { Assessment, CustomFieldValue } from "src/@types/ImpiricaPlatformApi";

export const useNeurapulse = () => {
	const { assessmentClient, customFieldValueClient, neurapulseClient } =
		useApi();

	const newNeurapulseAssessment = useCallback(
		async (
			organizationId: number,
			clientId: number,
			datasetId: number,
			assessmentPurposeId: number,
			customFieldValues?: CustomFieldValue[]
		): Promise<Assessment | null> => {
			const newAssessment = new Assessment({
				id: 0,
				originalId: Math.floor(Math.random() * -1000000), //Generate random negative number for new assessment
				clientId: clientId,
				datasetId: datasetId,
				assessmentPurposeId: assessmentPurposeId,
				dataLocked: false,
				migrated: false,
				consent: true,
				mobile: false,
				customFieldValues: customFieldValues,
			});

			let assessment = null;
			if (assessmentClient) {
				assessment = await assessmentClient.add(
					organizationId,
					datasetId,
					clientId,
					newAssessment,
					false
				);
			}

			return assessment;
		},
		[assessmentClient]
	);

	const addCustomFields = useCallback(
		async (
			organizationId: number,
			datasetId: number,
			customFieldValues: CustomFieldValue[]
		): Promise<CustomFieldValue[]> => {
			const addedCustomFields = await Promise.all(
				customFieldValues.map(async (cf) => {
					// Add the custom field value
					if (customFieldValueClient) {
						return await customFieldValueClient.add(
							cf,
							organizationId.toString(),
							datasetId.toString()
						);
					} else {
						throw new Error("Custom Field Value Client is not available");
					}
				})
			);
			return addedCustomFields;
		},
		[customFieldValueClient]
	);

	const updateCustomFields = useCallback(
		async (
			organizationId: number,
			datasetId: number,
			customFieldValues: CustomFieldValue[]
		): Promise<CustomFieldValue[]> => {
			const updatedCustomFields = await Promise.all(
				customFieldValues.map(async (cf) => {
					if (cf.id === undefined) {
						throw new Error("Custom Field Value ID is undefined");
					}

					// Update the custom field value
					if (customFieldValueClient) {
						return await customFieldValueClient.updatePUT(
							cf,
							organizationId.toString(),
							datasetId.toString(),
							cf.id!.toString()
						);
					} else {
						throw new Error("Custom Field Value Client is not available");
					}
				})
			);
			return updatedCustomFields;
		},
		[customFieldValueClient]
	);

	const generateLink = useCallback(
		async (organizationId: number, datasetId: number, assessmentId: number) => {
			if (neurapulseClient) {
				try {
					return await neurapulseClient.getMagicLink(
						organizationId,
						datasetId,
						assessmentId
					);
				} catch (error) {
					console.error("Error generating Neurapulse link");
				}
				return null;
			}
			return null;
		},
		[neurapulseClient]
	);

	return {
		newNeurapulseAssessment,
		addCustomFields,
		updateCustomFields,
		generateLink,
	};
};
